import { useState } from "react";
import { Plug, RefreshCw, Unplug } from "lucide-react";
import { Alert } from "@/components/common/Alert/Alert";
import { Button } from "@/components/shadcn/button";
import { ConfirmDialog } from "@/components/common/Dialog";
import { showErrorToast, showSuccessToast } from "@/hooks/use-toast";
import { disconnectMail, startMailConnect } from "../api";
import type { GraphConnection } from "../types";
import { AccountSettingsSectionHeading } from "./AccountSettingsSectionHeading";
import { ConnectionDetails, DetailItem } from "./ConnectionDetails";
import { ConnectionStatusBadge } from "./ConnectionStatusBadge";
import { MailSyncHelp } from "./MailSyncHelp";

type MailConnectionCardProps = {
  connection: GraphConnection;
  onChanged: () => Promise<void>;
};

const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
});

export const MailConnectionCard = ({ connection, onChanged }: MailConnectionCardProps) => {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const connect = async () => {
    setIsRedirecting(true);
    try {
      window.location.assign(await startMailConnect());
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : "Failed to start mail authorisation.");
      setIsRedirecting(false);
    }
  };

  const disconnect = async () => {
    setIsDisconnecting(true);
    try {
      await disconnectMail();
      showSuccessToast("Mail disconnected.");
      setConfirmOpen(false);
      await onChanged();
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : "Failed to disconnect mail.");
    } finally {
      setIsDisconnecting(false);
    }
  };

  const busy = isRedirecting || isDisconnecting;
  const isPaused = connection.isConnected && !connection.isEnabled;

  return (
    <div className="min-w-0">
      <AccountSettingsSectionHeading
        title="Mail"
        subtitle="One-way: emails involving your customers are filed in Klipboard CRM."
      />

      {connection.error && <Alert variant="error" persistent>{connection.error}</Alert>}

      <ConnectionDetails>
        <DetailItem label="Status">
          <ConnectionStatusBadge isConnected={connection.isConnected} isEnabled={connection.isEnabled} />
        </DetailItem>
        {connection.isConnected && (
          <>
            <DetailItem label="Microsoft account">{connection.email ?? "Not available"}</DetailItem>
            <DetailItem label="Connected">
              {connection.connectedDateUtc
                ? dateTimeFormatter.format(new Date(connection.connectedDateUtc))
                : "Not available"}
            </DetailItem>
          </>
        )}
      </ConnectionDetails>

      {isPaused && (
        <div className="mt-5">
          <Alert variant="warning" persistent>
            Filing is paused because Microsoft stopped accepting your saved sign-in - usually
            because your password changed or access was withdrawn. Reconnect to resume. Emails
            already filed are kept, and nothing in your mailbox is affected.
          </Alert>
        </div>
      )}

      {connection.isConnected && !isPaused && (
        <div className="mt-5">
          <Alert variant="information" persistent>
            Your mailbox is connected. We read your inbox and sent items only, and never send,
            change or delete anything in your mailbox.
          </Alert>
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-2">
        {connection.isConnected ? (
          <>
            <Button variant={isPaused ? "default" : "outline"} size="sm" onClick={connect} disabled={busy}>
              <RefreshCw className="size-4" aria-hidden />
              {isRedirecting
                ? "Redirecting..."
                : isPaused ? "Resume filing" : "Reconnect mail"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setConfirmOpen(true)} disabled={busy}>
              <Unplug className="size-4" aria-hidden />
              Disconnect mail
            </Button>
          </>
        ) : (
          <Button size="sm" onClick={connect} disabled={busy}>
            <Plug className="size-4" aria-hidden />
            {isRedirecting ? "Redirecting..." : "Connect mail"}
          </Button>
        )}
      </div>

      <div className="mt-6">
        <p className="mb-1 text-sm font-semibold">What gets filed</p>
        <p className="my-1 text-sm text-muted-foreground">
          Once you connect your Microsoft mailbox, Klipboard CRM files relevant emails against
          the companies and contacts they involve. This only works one way - unlike the
          calendar, nothing is ever sent back to your mailbox.
        </p>
        <ul className="my-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          <li>Emails you receive and emails you send are both filed.</li>
          <li>
            An email is only filed if one of its addresses matches a contact or company in
            Klipboard CRM, so newsletters and personal mail stay out.
          </li>
          <li>Emails only between colleagues aren't filed - there's no customer to file them against.</li>
          <li>Your existing mail history isn't copied in; filing starts when you connect.</li>
          <li>We never send, reply to, move, change or delete anything in your mailbox.</li>
        </ul>

        <div className="mt-4">
          <MailSyncHelp />
        </div>
      </div>

      <hr className="pb-4 mt-4" />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        hideTrigger
        title="Disconnect mail?"
        description="Disconnecting stops new emails being filed - reconnecting later starts from that point, so emails from in between won't be picked up. Emails already filed are kept, and your mailbox is left untouched."
        confirmText="Disconnect"
        loading={isDisconnecting}
        onConfirm={disconnect}
      />
    </div>
  );
};
